import { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { resetPasswordApi } from './forgotPassword';
import toast from 'react-hot-toast';

interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const route = useParams();

  const validatePassword = (password: any) => {
    const minLength = password.length >= 6;
    const hasNumber = /\d/.test(password);
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      minLength,
      hasNumber,
      hasLetter,
      hasSpecial,
      isValid: minLength && hasNumber && hasLetter && hasSpecial
    };
  };

  const passwordChecks = validatePassword(newPassword);

  const handleSubmit = async () => {
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      toast.error('Password does not meet all requirements');
      return;
    }

    if (!confirmPassword) {
      toast.error('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const payload = route.resetToken?.split("&&")[0];
    if (payload) {
      const toastId = toast.loading('Resetting password...');
      try {
        const res = await resetPasswordApi(newPassword, payload) as ResetPasswordResponse;
        if (res.success) {
          toast.success(res.message, { id: toastId });
          setIsSuccess(true);
        } else {
          toast.error(res.message, { id: toastId });
        }
      } catch (error: any) {
        toast.error(error.message || "An unexpected error occurred.", { id: toastId });
      }
    }
  };

  const handleBackToLogin = () => {
    if (route.resetToken?.split("&&")[1] === "reset-instructor-password") {
      window.location.href = 'https://partner.metafitwellness.com/';
    } else {
      window.location.href = '/login';
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}>
        <div className="absolute top-0 left-0 w-64 h-64 bg-teal-200 rounded-full opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-200 rounded-full opacity-20 translate-x-1/3 translate-y-1/3"></div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-teal-400 to-cyan-500 rounded-full mb-6">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-3">Password Reset Successful!</h2>
            <p className="text-gray-600 mb-8">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>

            <button
              onClick={handleBackToLogin}
              className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
    }}>
      <div className="absolute top-0 left-0 w-64 h-64 bg-teal-200 rounded-full opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-200 rounded-full opacity-20 translate-x-1/3 translate-y-1/3"></div>

      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
        <button
          onClick={handleBackToLogin}
          className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-teal-500 rounded-full mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-2">Create A Strong Password</h1>
          <p className="text-gray-600 text-sm">
            Your password must be at least 6 characters and should include a combination of numbers, letters and special characters (!$@%).
          </p>
        </div>

        <div className="space-y-5">
          {/* New Password Field */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showNewPassword ? "text" : "password"}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter new password"
              />
              <button
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          {newPassword && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
              <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  {passwordChecks.minLength ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={`text-xs ${passwordChecks.minLength ? 'text-green-600' : 'text-gray-600'}`}>
                    At least 6 characters
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordChecks.hasNumber ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={`text-xs ${passwordChecks.hasNumber ? 'text-green-600' : 'text-gray-600'}`}>
                    Contains a number
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordChecks.hasLetter ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={`text-xs ${passwordChecks.hasLetter ? 'text-green-600' : 'text-gray-600'}`}>
                    Contains letters
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordChecks.hasSpecial ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                  <span className={`text-xs ${passwordChecks.hasSpecial ? 'text-green-600' : 'text-gray-600'}`}>
                    Contains special characters (!$@%)
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                placeholder="Confirm new password"
              />
              <button
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className="w-full py-3 px-4 bg-gradient-to-r from-yellow-400 via-teal-400 to-cyan-500 text-white rounded-lg font-medium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 mt-6"
          >
            Reset Password
          </button>
        </div>
      </div>
    </div>
  );
}
